/**
 * Shared utility to parse stringified JSON conclusions from diagnostic reports
 * into human-readable formatted sections.
 *
 * Supports two formats:
 * 1. Detailed consultation format (patient_summary, chief_complaint, etc.)
 * 2. SOAP note format (subjective, objective, assessment, plan)
 */

import { toStructuredMedication, formatMedicationString } from '@/lib/utils/medications';
import {
  formatDiagnosisEntry,
  formatInvestigationEntry,
  formatRelationConditionEntry,
  formatRelationConditionList,
  getFamilyHistoryItems,
  isInvestigationItem,
  isRelationConditionItem,
  splitCommaSeparatedText,
  splitLabelValueItem,
  splitMedicalNarrativeToBullets,
  splitProseToBullets,
  type RelationConditionEntry,
} from '@/lib/utils/clinicalText';

export interface ConclusionSection {
  label: string;
  content: string;
  type?: 'text' | 'table' | 'list';
  columns?: Array<{ key: string; label: string }>;
  rows?: Array<Record<string, string>>;
  /** Individual entries for type: 'list' sections, rendered one per bullet. */
  items?: string[];
}

const formatFieldItems = (items: unknown[]): string[] =>
  items
    .map((item) => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null && isRelationConditionItem(item)) {
        return formatRelationConditionEntry(item);
      }
      if (typeof item === 'object' && item !== null && isInvestigationItem(item)) {
        return formatInvestigationEntry(item);
      }
      if (typeof item === 'object' && item !== null && 'diagnosis' in item) {
        // formatDiagnosisEntry already appends notes itself.
        return formatDiagnosisEntry(item as { diagnosis: unknown; notes?: unknown });
      }
      // Physical examination entries, e.g. { system, type, value, notes } —
      // same "type: value" convention CaseSheetView/CaseSheetPage use when
      // flattening this field, so this stays consistent with the Case Sheet.
      if (typeof item === 'object' && item !== null && 'type' in item && 'value' in item) {
        const { type, value, notes } = item as { type: unknown; value: unknown; notes?: unknown };
        const base = `${String(type)}: ${String(value)}`;
        return notes ? `${base} — ${String(notes)}` : base;
      }
      if (typeof item === 'object' && item !== null) {
        return formatMedicationObject(item as Record<string, unknown>);
      }
      return String(item);
    })
    .filter(Boolean);

/**
 * Extract value from nested format { value, timestamp, explanation } as a list of
 * individual entries (rather than one comma-joined string) — used to render fields
 * like investigations as separate bullets instead of a flowing paragraph.
 */
export const getFieldValueItems = (field: unknown): string[] => {
  if (!field) return [];
  if (typeof field === 'string') return [field];
  if (Array.isArray(field)) {
    if (field.length > 0 && field.every(isRelationConditionItem)) {
      return [formatRelationConditionList(field)];
    }
    return formatFieldItems(field);
  }
  if (typeof field === 'object' && field !== null && 'value' in field) {
    const val = (field as { value?: string | string[] | unknown[] }).value;
    if (!val) return [];
    if (typeof val === 'string') return [val];
    if (Array.isArray(val)) {
      if (val.length > 0 && (val as unknown[]).every(isRelationConditionItem)) {
        return [formatRelationConditionList(val as unknown as RelationConditionEntry[])];
      }
      return formatFieldItems(val);
    }
  }
  return [];
};

/**
 * Extract value from nested format { value, timestamp, explanation }. Items
 * are joined with '\n', not ', ' — each array element is already the
 * backend's own discrete bullet boundary, and a comma join is ambiguous
 * whenever an item has its own internal comma (e.g. "Patient has a history
 * of high BP, high cholesterol, asthma..." as ONE patient_summary bullet),
 * wrongly letting downstream splitters (splitCommaSeparatedText,
 * splitProseToBullets, splitMedicalNarrativeToBullets — which already treat
 * '\n' as the authoritative boundary) break one item into several. Mirrors
 * the same fix applied to extractSessionField/getSessionFieldValue.
 */
export const getFieldValue = (field: unknown): string => getFieldValueItems(field).join('\n');

/**
 * True when `field` is a bare array, or the modern backend's
 * `{ value, timestamp, source, source_phrase, explanation }` envelope wrapping
 * one. Used to prefer the raw array's own item boundaries (getFieldValueItems)
 * over flattening to a comma-joined string and re-splitting it — the latter
 * corrupts any item that itself contains a comma or semicolon (e.g. "Fever
 * onset 3 days ago, worse in the evenings" would wrongly become two bullets).
 */
const hasArrayValue = (field: unknown): boolean => {
  if (Array.isArray(field)) return true;
  if (typeof field === 'object' && field !== null && 'value' in field) {
    return Array.isArray((field as { value?: unknown }).value);
  }
  return false;
};

/**
 * Format a medication object into a readable string.
 * Reuses the same logic as CaseSheetView to ensure consistency.
 */
const formatMedicationObject = (obj: Record<string, unknown>): string => {
  const structured = toStructuredMedication(obj);
  if (structured) {
    return formatMedicationString(structured);
  }
  // Fallback: try to extract any readable string from the object
  const readableParts: string[] = [];
  for (const [, value] of Object.entries(obj)) {
    if (value == null) continue;
    const strValue =
      typeof value === 'string'
        ? value
        : typeof value === 'object' &&
            value !== null &&
            'value' in (value as Record<string, unknown>)
          ? String((value as Record<string, unknown>).value ?? '')
          : String(value);
    if (strValue && strValue !== '[object Object]') {
      readableParts.push(strValue);
    }
  }
  return readableParts.length > 0 ? readableParts.join(', ') : JSON.stringify(obj);
};

/**
 * Parse medications field into table rows for structured display.
 * Exported so callers that already hold a medications array (e.g. the case sheet's
 * StructuredMedication[]) can build the same table shape without going through JSON.
 */
export const parseMedicationsToTable = (
  field: unknown
): {
  columns: Array<{ key: string; label: string }>;
  rows: Array<Record<string, string>>;
} | null => {
  const medications: unknown[] = [];

  if (Array.isArray(field)) {
    medications.push(...field);
  } else if (field && typeof field === 'object' && 'value' in field) {
    const val = (field as { value?: unknown }).value;
    if (Array.isArray(val)) {
      medications.push(...val);
    }
  }

  if (medications.length === 0) return null;

  const columns = [
    { key: 'name', label: 'Generic name/Strength/Form' },
    { key: 'medicineType', label: 'Type' },
    { key: 'dosage', label: 'Dosage' },
    { key: 'frequency', label: 'Schedule' },
    { key: 'route', label: 'Route' },
    { key: 'duration', label: 'Days' },
    { key: 'relationWithMeal', label: 'Relation with meal' },
    { key: 'comments', label: 'Comments' },
    { key: 'itemCode', label: 'Item Code' },
  ];

  const rows: Array<Record<string, string>> = [];
  for (const med of medications) {
    const structured = toStructuredMedication(med);
    if (!structured) continue;
    rows.push({
      name: structured.medication || '',
      medicineType: structured.medicineType || '',
      dosage: structured.dosage || '',
      frequency: structured.frequency || '',
      route: structured.route || '',
      duration: structured.duration || '',
      relationWithMeal: structured.relationWithMeal || '',
      comments: structured.additionalInfo || '',
      itemCode: structured.itemCode || '',
    });
  }

  if (rows.length === 0) return null;
  return { columns, rows };
};

/**
 * Parse the ICD-11 symptom codes nested inside history_of_present_illness into
 * table rows, mirroring the Diagnosis table shown in the Case Sheet / Patient Record.
 */
const parseSymptomCodesToTable = (
  field: unknown
): {
  columns: Array<{ key: string; label: string }>;
  rows: Array<Record<string, string>>;
} | null => {
  const raw =
    field && typeof field === 'object' ? (field as Record<string, unknown>).symptom_codes : null;
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    { key: 'code', label: 'Code' },
  ];

  const rows: Array<Record<string, string>> = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const obj = item as Record<string, unknown>;
    const symptom = typeof obj.symptom === 'string' ? obj.symptom : '';
    if (!symptom) continue;
    const symptomId = typeof obj.symptom_id === 'string' ? obj.symptom_id : '';
    const parts = symptomId.split('|');
    // "SYSTEM|CODE|DESCRIPTION" (e.g. "ICD-10|A78|Q fever") when the system is known;
    // falls back to treating the whole string as the code for older/unqualified data.
    const hasSystem = parts.length > 1;
    const type = hasSystem ? parts[0] : 'ICD-11';
    const code = hasSystem ? parts[1] : symptomId;
    const description = (hasSystem ? parts.slice(2).join('|') : '') || '';
    rows.push({
      name: symptom,
      type,
      code: description ? `${code} — ${description}` : code,
    });
  }

  return rows.length > 0 ? { columns, rows } : null;
};

/**
 * Parse a JSON conclusion string into structured sections.
 * Returns null if the string is not valid JSON or has no recognized fields.
 */
export const parseConclusion = (conclusion: string): ConclusionSection[] | null => {
  try {
    const data = JSON.parse(conclusion);
    if (typeof data !== 'object' || data === null) return null;

    const sections: ConclusionSection[] = [];

    // Check if this is SOAP note format (subjective, objective, assessment, plan).
    // `assessment` alone is NOT a reliable SOAP signal — Ayurveda backend
    // reports written directly (bypassing the Case Sheet save flow) also
    // carry a top-level `assessment` field alongside chief_complaint,
    // personal_history, diagnosis, etc. Treating any `assessment` presence
    // as "this is a legacy SOAP-only note" caused the whole detailed
    // format below (and the Ayurveda-specific sections further down) to be
    // skipped entirely, so the PDF/Patient Record showed only "Assessment
    // (A): ..." and nothing else. Only `subjective`/`objective`/`plan` are
    // exclusive to the legacy SOAP shape, so gate on those, and only when
    // there's no sign of the richer detailed/Ayurveda format alongside it.
    const looksLikeDetailedFormat =
      data.chief_complaint || data.patient_summary || data.assessment_and_diagnosis !== undefined;
    // Self-gated the same way as the Ayurveda-specific sections further
    // down — only an Ayurveda consultation's report ever carries these keys.
    const isAyurvedaReport = Boolean(
      data.diagnosis || data.personal_history || data.astha_sthaana_pareeksha
    );
    if ((data.subjective || data.objective || data.plan) && !looksLikeDetailedFormat) {
      const soapFieldMap: [string, string][] = [
        ['subjective', 'Subjective (S)'],
        ['objective', 'Objective (O)'],
        ['assessment', 'Assessment (A)'],
        ['plan', 'Plan (P)'],
      ];
      for (const [key, label] of soapFieldMap) {
        const value = getFieldValue(data[key]);
        if (value) {
          sections.push({ label, content: value });
        }
      }
      return sections.length > 0 ? sections : null;
    }

    // Detailed consultation format
    const fieldMap: [string, string][] = [
      ['patient_summary', 'Patient Summary'],
      ['chief_complaint', 'Chief Complaint'],
      ['history_of_present_illness', 'Clinical Symptoms'],
      ['past_medical_surgical_history', 'Medical History'],
      ['family_history', 'Family History'],
      ['physical_examination', 'General Examination'],
      ['assessment_and_diagnosis', 'Assessment & Diagnosis'],
      ['investigations_orders', 'Investigations'],
      ['medications', 'Medications'],
      ['treatment_plan_management', 'Treatment Plan'],
      ['allergies', 'Allergies'],
      ['social_history', 'Social History'],
      ['review_of_systems', 'Review of Systems'],
      ['medication_history', 'Medication History'],
      ['pediatric_milestone', 'Pediatric Milestone'],
      ['adt_order', 'ADT Order'],
    ];

    for (const [key, label] of fieldMap) {
      // Ayurveda-only: skip the generic Medications table entirely — the
      // "Ayurvedic Treatment — Medicines" table further down (sourced from
      // prescribed_medicines) replaces it, same convention as the ICD-11
      // diagnosis table skip above.
      if (key === 'medications' && isAyurvedaReport) {
        continue;
      }

      // Special handling for investigations - render as a bullet list, one entry per line,
      // instead of collapsing every investigation into a single comma-joined sentence.
      // Prefer the raw array's own items (getFieldValueItems) so an entry containing
      // a comma (e.g. "Blood glucose: 120, fasting") isn't itself split apart. Once a
      // report is saved this field is flattened to a single comma-joined string (see
      // CaseSheetPage.tsx's save flow), so getFieldValueItems on the string would
      // return it as ONE unsplit item — fall back to splitCommaSeparatedText then.
      if (key === 'investigations_orders') {
        const rawValue = data[key];
        const items = hasArrayValue(rawValue)
          ? getFieldValueItems(rawValue)
          : splitCommaSeparatedText(getFieldValue(rawValue));
        if (items.length > 0) {
          sections.push({ label, content: items.join('\n'), type: 'list', items });
          continue;
        }
      }

      // Special handling for physical examination - one bullet per structured
      // exam entry (e.g. { type: "exam", value: "mild murmur" } -> "exam: mild
      // murmur"), same convention as investigations above. Same
      // getFieldValueItems-vs-flattened-string reasoning as investigations.
      if (key === 'physical_examination') {
        // Drop the generic "exam:" placeholder label some AI-structured
        // entries carry — it names the entry's kind, not anything
        // informative to show, so this renders as a plain bullet instead of
        // "exam: <finding>". A real label (e.g. "Heart: ...") passes through.
        const rawValue = data[key];
        const rawItems = hasArrayValue(rawValue)
          ? getFieldValueItems(rawValue)
          : splitCommaSeparatedText(getFieldValue(rawValue));
        const items = rawItems.map((item) => {
          const { label: itemLabel, value } = splitLabelValueItem(item);
          return itemLabel ? `${itemLabel}: ${value}` : value;
        });
        if (items.length > 0) {
          sections.push({ label, content: items.join('\n'), type: 'list', items });
          continue;
        }
      }

      // Special handling for chief complaint - split the comma-joined value
      // (e.g. "Fever, chest pain, and cough for 5 days") into one bullet per
      // complaint, same convention as investigations above.
      if (key === 'chief_complaint') {
        const rawValue = data[key];
        const items = hasArrayValue(rawValue)
          ? getFieldValueItems(rawValue)
          : splitCommaSeparatedText(getFieldValue(rawValue));
        if (items.length > 0) {
          sections.push({ label, content: items.join('\n'), type: 'list', items });
          continue;
        }
      }

      // Special handling for allergies - same bullet-per-entry treatment as
      // chief complaint above (e.g. ["Penicillin", "Calpol"] → one bullet each).
      if (key === 'allergies') {
        const rawValue = data[key];
        const items = hasArrayValue(rawValue)
          ? getFieldValueItems(rawValue)
          : splitCommaSeparatedText(getFieldValue(rawValue));
        if (items.length > 0) {
          sections.push({ label, content: items.join('\n'), type: 'list', items });
          continue;
        }
      }

      // Special handling for family history - one bullet per family member, no
      // leading number (the bullet replaces it), each still showing its
      // Relation/Condition/Notes on their own lines. getFamilyHistoryItems
      // handles both the raw relation/condition array AND the already-
      // flattened numbered text a saved report actually has by the time it
      // gets here (a Case Sheet save collapses the array into a plain string —
      // see CaseSheetPage.tsx's buildConclusionData — so re-splitting that
      // text is what makes this work for real saved reports, not just live
      // in-memory data).
      if (key === 'family_history') {
        const items = getFamilyHistoryItems(data[key]);
        if (items.length > 0) {
          sections.push({ label, content: items.join('\n\n'), type: 'list', items });
          continue;
        }
      }

      // Special handling for assessment/diagnosis - same bullet-per-entry
      // treatment as chief complaint above (e.g. "Ulcer, Angina,
      // Gastroesophageal reflux disease" → one bullet per diagnosis).
      // Ayurveda backend-direct saves often leave this field empty and put
      // the real content under the plain `assessment` key instead — fall
      // back to it so this section isn't blank (same fallback the generic
      // path below used to apply).
      if (key === 'assessment_and_diagnosis') {
        // hasArrayValue is true for an empty { value: [] } envelope too (it's
        // still an array, just empty), so check the primary items' actual
        // length before falling back to `assessment` — otherwise an
        // Ayurveda-direct save that leaves assessment_and_diagnosis as an
        // empty array wrapper would never reach the fallback, even though
        // `assessment` holds the real diagnosis text.
        const primaryItems = hasArrayValue(data[key])
          ? getFieldValueItems(data[key])
          : splitCommaSeparatedText(getFieldValue(data[key]));
        const items =
          primaryItems.length > 0
            ? primaryItems
            : splitCommaSeparatedText(getFieldValue(data['assessment']));
        if (items.length > 0) {
          sections.push({ label, content: items.join('\n'), type: 'list', items });
        }

        // ICD-11 diagnosis codes are nested inside history_of_present_illness but
        // rendered as their own table, positioned after Assessment & Diagnosis
        // (matching the Case Sheet / Patient Record layout). Ayurveda-only
        // reports skip this generic table — the Ayurvedic/Allopathic diagnosis
        // section further down replaces it.
        if (!isAyurvedaReport) {
          const diagnosisTable = parseSymptomCodesToTable(data['history_of_present_illness']);
          if (diagnosisTable) {
            sections.push({
              label: 'Diagnosis',
              content: diagnosisTable.rows
                .map((r) =>
                  diagnosisTable.columns
                    .map((c) => r[c.key])
                    .filter(Boolean)
                    .join(' | ')
                )
                .filter(Boolean)
                .join('\n'),
              type: 'table',
              columns: diagnosisTable.columns,
              rows: diagnosisTable.rows,
            });
          }
        }
        continue;
      }

      // Special handling for Medical History — it's a free-text narrative
      // (AI-extracted sentences concatenated together, sometimes hand-edited),
      // not an itemized list, so it needs sentence-aware splitting plus
      // per-sentence list-comma splitting rather than one giant bullet. It can
      // also carry an embedded "Lab investigations reviewed: ..."/"Blood test
      // ... reviewed: ..." clause, pulled into its own "Lab Investigations
      // Reviewed" section instead of staying mixed into the main bullets. Same
      // rules as the Case Sheet PDF/clipboard export (caseSheetSummary.ts) and
      // the live Case Sheet screen (CaseSheetView.tsx) — see
      // splitMedicalNarrativeToBullets for the full logic.
      if (key === 'past_medical_surgical_history') {
        // getFieldValue already extracts/joins a plain string regardless of
        // whether the raw value is a legacy string or a modern array —
        // splitMedicalNarrativeToBullets then re-splits it properly (one
        // bullet per sentence/list-comma, with the lab clause pulled out).
        const { mainItems, labItems } = splitMedicalNarrativeToBullets(getFieldValue(data[key]));
        if (mainItems.length > 0) {
          sections.push({ label, content: mainItems.join('\n'), type: 'list', items: mainItems });
        }
        if (labItems.length > 0) {
          sections.push({
            label: 'Lab Investigations Reviewed',
            content: labItems.join('\n'),
            type: 'list',
            items: labItems,
          });
        }
        continue;
      }

      // Special handling for medications - render as table
      if (key === 'medications') {
        const tableData = parseMedicationsToTable(data[key]);
        if (tableData) {
          sections.push({
            label,
            content: tableData.rows
              .map((r) =>
                tableData.columns
                  .map((c) => r[c.key])
                  .filter(Boolean)
                  .join(' | ')
              )
              .filter(Boolean)
              .join('\n'),
            type: 'table',
            columns: tableData.columns,
            rows: tableData.rows,
          });
          continue;
        }
      }

      // Everything else (Treatment Plan, History of Present Illness, Social
      // History, Review of Systems, Medication History, Pediatric Milestone,
      // ADT Order). The modern backend sends these as a native string[] — one
      // bullet per array element, so render each item as its own bullet (via
      // getFieldValueItems' own array boundaries) rather than flattening to
      // one comma-joined prose bullet. A legacy plain-string payload is split
      // sentence-by-sentence plus per-sentence list-comma (same rules as
      // Medical History minus its lab-clause detection) instead of becoming
      // one run-on bullet holding the entire narrative.
      const items = hasArrayValue(data[key])
        ? getFieldValueItems(data[key])
        : splitProseToBullets(getFieldValue(data[key]));
      if (items.length > 0) {
        sections.push({ label, content: items.join('\n'), type: 'list', items });
      }
    }

    // Ayurveda-only Personal History, Diagnosis (Ayurvedic/Allopathic), and
    // Treatment (Medicines/Treatment Principle/Treatment Given). These live
    // under their own top-level `personal_history`/`diagnosis`/`treatment`
    // keys, only ever written by an Ayurveda consultation's Case Sheet save
    // — so no explicit department flag is needed here, each section is
    // self-gated by its key's presence, same convention as the rest of this
    // parser.
    // Chief Complaint Duration/Mode of Onset, and the History cluster
    // (Past Illness/Treatment History/Family-Social-Occupational) — self-
    // gated the same way, present on both Case Sheet-saved and
    // backend-direct-saved reports.
    const chiefComplaintExtraLines: string[] = [];
    if (typeof data.chief_complaint_duration === 'string' && data.chief_complaint_duration) {
      chiefComplaintExtraLines.push(`Duration: ${data.chief_complaint_duration}`);
    }
    if (
      typeof data.chief_complaint_mode_of_onset === 'string' &&
      data.chief_complaint_mode_of_onset
    ) {
      chiefComplaintExtraLines.push(`Mode of Onset: ${data.chief_complaint_mode_of_onset}`);
    }
    if (chiefComplaintExtraLines.length > 0) {
      sections.push({
        label: 'Chief Complaint — Duration & Mode of Onset',
        content: chiefComplaintExtraLines.join('\n'),
      });
    }

    const historyLines: string[] = [];
    if (typeof data.past_illness === 'string' && data.past_illness) {
      historyLines.push(`History of Past Illness: ${data.past_illness}`);
    }
    if (typeof data.treatment_history === 'string' && data.treatment_history) {
      historyLines.push(`Treatment History Of: ${data.treatment_history}`);
    }
    if (typeof data.family_social_occupational === 'string' && data.family_social_occupational) {
      historyLines.push(`Family/ Social/ Occupational: ${data.family_social_occupational}`);
    }
    if (historyLines.length > 0) {
      sections.push({ label: 'History', content: historyLines.join('\n') });
    }

    const asthaSthaanaPareeksha = data.astha_sthaana_pareeksha as
      | Record<string, string | null | undefined>
      | undefined;
    if (asthaSthaanaPareeksha && typeof asthaSthaanaPareeksha === 'object') {
      const paramLabels: Array<[string, string]> = [
        ['nadi', 'Nadi'],
        ['mootram', 'Mootram'],
        ['malam', 'Malam'],
        ['jihva', 'Jihva'],
        ['sabdam', 'Sabdam'],
        ['sparsham', 'Sparsham'],
        ['drik', 'Drik'],
        ['akrithi', 'Akrithi'],
      ];
      const lines = paramLabels
        .filter(([key]) => asthaSthaanaPareeksha[key])
        .map(([key, label]) => `${label}: ${asthaSthaanaPareeksha[key]}`);
      if (lines.length > 0) {
        sections.push({ label: 'Astha Sthaana Pareeksha', content: lines.join('\n') });
      }
    }

    const ayurvedaPersonalHistory = data.personal_history as
      | {
          diet?: string | null;
          appetite?: string | null;
          sleep?: string | null;
          exercise?: string | null;
          bowel?: string | null;
          bladder?: string | null;
          addiction?: string | null;
          menstrual?: string | null;
        }
      | undefined;
    if (ayurvedaPersonalHistory && typeof ayurvedaPersonalHistory === 'object') {
      const fieldMap: Array<[keyof typeof ayurvedaPersonalHistory, string]> = [
        ['diet', 'Diet'],
        ['appetite', 'Appetite'],
        ['sleep', 'Sleep'],
        ['exercise', 'Exercise'],
        ['bowel', 'Bowel'],
        ['bladder', 'Bladder'],
        ['addiction', 'Addiction'],
        ['menstrual', 'Menstrual'],
      ];
      const lines = fieldMap
        .filter(([key]) => ayurvedaPersonalHistory[key])
        .map(([key, label]) => `${label}: ${ayurvedaPersonalHistory[key]}`);
      if (lines.length > 0) {
        sections.push({ label: 'Personal History', content: lines.join('\n') });
      }
    }

    // Diagnosis can appear in two shapes depending on who wrote the report:
    // `{ayurvedic, allopathic}` when the Case Sheet's own Save flow wrote it,
    // or `{ayurvedic_diagnosis, allopathic_diagnosis}` when the CDSS backend
    // wrote the DiagnosticReport directly (bypassing the Case Sheet). Check
    // both.
    const ayurvedaDiagnosis = data.diagnosis as
      | {
          ayurvedic?: string | null;
          allopathic?: string | null;
          ayurvedic_diagnosis?: string | null;
          allopathic_diagnosis?: string | null;
        }
      | undefined;
    if (ayurvedaDiagnosis && typeof ayurvedaDiagnosis === 'object') {
      const ayurvedic = ayurvedaDiagnosis.ayurvedic || ayurvedaDiagnosis.ayurvedic_diagnosis;
      const allopathic = ayurvedaDiagnosis.allopathic || ayurvedaDiagnosis.allopathic_diagnosis;
      const lines: string[] = [];
      if (ayurvedic) lines.push(`Ayurvedic: ${ayurvedic}`);
      if (allopathic) lines.push(`Allopathic: ${allopathic}`);
      if (lines.length > 0) {
        sections.push({ label: 'Ayurvedic Diagnosis', content: lines.join('\n') });
      }
    }

    // Treatment, similarly, can be nested under a `treatment` object (Case
    // Sheet save) or spread across top-level keys (backend-direct save:
    // `prescribed_medicines`, `treatment_principle` as an array, no
    // `treatment` object at all) — read both, never gating on `data.treatment`
    // existing, so a backend-direct report still shows its Treatment section.
    const nestedTreatment = data.treatment as
      | {
          medicines?: string | string[] | null;
          treatmentPrinciple?: string | null;
          prescribedMedicines?: Array<Record<string, string | null | undefined>> | null;
        }
      | undefined;
    const medicinesTable =
      nestedTreatment?.prescribedMedicines ??
      (data.prescribed_medicines as Array<Record<string, string | null | undefined>> | undefined);
    if (Array.isArray(medicinesTable) && medicinesTable.length > 0) {
      const columns = [
        { key: 'name', label: 'Medicine Name' },
        { key: 'form', label: 'Form' },
        { key: 'dose', label: 'Dosage' },
        { key: 'frequency', label: 'Frequency' },
        { key: 'duration', label: 'Duration' },
        { key: 'anupana', label: 'Anupana' },
        { key: 'timing', label: 'Timing' },
        { key: 'additional_info', label: 'Additional Info' },
      ];
      const rows = medicinesTable.map((med) =>
        Object.fromEntries(columns.map((c) => [c.key, med[c.key] || '']))
      );
      sections.push({
        label: 'Ayurvedic Treatment — Medicines',
        type: 'table',
        columns,
        rows,
        content: rows
          .map((r) =>
            columns
              .map((c) => r[c.key])
              .filter(Boolean)
              .join(' | ')
          )
          .join('\n'),
      });
    } else if (nestedTreatment?.medicines) {
      const medicinesText = Array.isArray(nestedTreatment.medicines)
        ? nestedTreatment.medicines.join(', ')
        : nestedTreatment.medicines;
      if (medicinesText) {
        sections.push({ label: 'Ayurvedic Treatment — Medicines', content: medicinesText });
      }
    }

    // Prefer the Vaidya's own literal quote (`treatment_principle_stated`)
    // over the AI-classified tag list (`treatment_principle`), same
    // precedence as CaseSheetPage.tsx.
    const treatmentPrinciple =
      nestedTreatment?.treatmentPrinciple ||
      (typeof data.treatment_principle_stated === 'string'
        ? data.treatment_principle_stated
        : undefined) ||
      (Array.isArray(data.treatment_principle)
        ? (data.treatment_principle as unknown[]).filter((v) => typeof v === 'string').join(', ')
        : (data.treatment_principle as string | undefined));
    if (treatmentPrinciple) {
      sections.push({
        label: 'Ayurvedic Treatment — Treatment Principle',
        content: treatmentPrinciple,
      });
    }

    // Treatment Given has no text of its own — same as the Case Sheet's
    // Treatment Given, it's just a heading directly containing its two
    // nested sub-lists, Diet and Lifestyle. `diet_recommendations`/
    // `lifestyle_recommendations` is the Case Sheet's saved-alias shape;
    // `diet_modifications`/`lifestyle_modifications` is the raw backend key.
    const dietItems =
      getFieldValueItems(data['diet_recommendations']).length > 0
        ? getFieldValueItems(data['diet_recommendations'])
        : getFieldValueItems(data['diet_modifications']);
    const lifestyleItems =
      getFieldValueItems(data['lifestyle_recommendations']).length > 0
        ? getFieldValueItems(data['lifestyle_recommendations'])
        : getFieldValueItems(data['lifestyle_modifications']);
    if (dietItems.length > 0 || lifestyleItems.length > 0) {
      const treatmentGivenLines: string[] = [];
      if (dietItems.length > 0) {
        treatmentGivenLines.push('Diet:');
        dietItems.forEach((item) => treatmentGivenLines.push(`  - ${item}`));
      }
      if (lifestyleItems.length > 0) {
        if (dietItems.length > 0) treatmentGivenLines.push('');
        treatmentGivenLines.push('Lifestyle:');
        lifestyleItems.forEach((item) => treatmentGivenLines.push(`  - ${item}`));
      }
      sections.push({
        label: 'Ayurvedic Treatment — Treatment Given',
        content: treatmentGivenLines.join('\n'),
      });
    }

    // Group diet_advice and follow_up_plan under "Advice & Follow Up". Rendered
    // as a 'list' section (sub-heading lines + bulleted items) so the bullets
    // get the exact same indentation/marker as every other bulleted PDF section,
    // instead of approximating it with manually space-padded text.
    // A single item can itself contain embedded newlines (a plain multi-line
    // string value rather than a true array of separate entries) — split
    // those back out so each line becomes its own bullet instead of the
    // first line "absorbing" the bullet and the rest rendering as unbulleted
    // wrap-continuation text.
    const splitEmbeddedLines = (items: string[]): string[] =>
      items.flatMap((item) =>
        item
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
      );
    const dietAdviceItems = splitEmbeddedLines(getFieldValueItems(data['diet_advice']));
    const followUpItems = splitEmbeddedLines(getFieldValueItems(data['follow_up_plan']));
    if (dietAdviceItems.length > 0 || followUpItems.length > 0) {
      const adviceItems: string[] = [];
      if (dietAdviceItems.length > 0) {
        adviceItems.push('Diet Advice:');
        adviceItems.push(...dietAdviceItems);
      }
      if (followUpItems.length > 0) {
        adviceItems.push('Follow Up Plan:');
        adviceItems.push(...followUpItems);
      }
      sections.push({
        label: 'Advice & Follow Up',
        content: '',
        type: 'list',
        items: adviceItems,
      });
    }

    return sections.length > 0 ? sections : null;
  } catch {
    return null;
  }
};

/**
 * Format a JSON conclusion string into human-readable plain text.
 * Falls back to the raw string if it can't be parsed.
 */
export const formatConclusionAsText = (conclusion: string): string => {
  const sections = parseConclusion(conclusion);
  if (!sections) return conclusion;

  return sections.map((s) => `${s.label}:\n${s.content}`).join('\n\n');
};
